import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Box,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const toDateStr = (d) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export default function ConvertToSickDialog({ open, absence, onClose, onConfirm }) {
    const [splitStart, setSplitStart] = useState(null);
    const [splitEnd, setSplitEnd] = useState(null);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open && absence) {
            const start = new Date(absence.startDate + 'T00:00:00');
            const end = new Date(absence.endDate + 'T00:00:00');
            setSplitStart(start);
            setSplitEnd(end);
            setReason('');
        }
    }, [open, absence]);

    if (!absence) return null;

    const minDate = new Date(absence.startDate + 'T00:00:00');
    const maxDate = new Date(absence.endDate + 'T00:00:00');

    const isValid = splitStart && splitEnd && splitEnd >= splitStart;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #EEF2F6', pb: 1.5 }}>
                Convert to Sick Leave
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                    Select the sick days within the vacation period. Those days will be converted to sick leave and returned to the vacation balance.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', mb: 0.75 }}>From</Typography>
                        <DatePicker
                            selected={splitStart}
                            minDate={minDate} maxDate={splitEnd || maxDate}
                            onChange={d => { setSplitStart(d); if (splitEnd < d) setSplitEnd(d); }}
                            dateFormat="dd.MM.yyyy"
                            customInput={<TextField size="small" fullWidth />}
                        />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', mb: 0.75 }}>To</Typography>
                        <DatePicker
                            selected={splitEnd}
                            minDate={splitStart || minDate} maxDate={maxDate}
                            onChange={setSplitEnd}
                            dateFormat="dd.MM.yyyy"
                            customInput={<TextField size="small" fullWidth />}
                        />
                    </Box>
                </Box>

                <TextField
                    label="Reason (optional)"
                    size="small" fullWidth multiline rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. flu, doctor visit"
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6', gap: 1 }}>
                <Button onClick={onClose} sx={{ color: '#64748B', textTransform: 'none' }}>Cancel</Button>
                <Button
                    variant="contained" disabled={!isValid}
                    onClick={() => onConfirm(toDateStr(splitStart), toDateStr(splitEnd), reason.trim() || null)}
                    sx={{ bgcolor: '#D97706', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                >
                    Convert to Sick Leave
                </Button>
            </DialogActions>
        </Dialog>
    );
}
